import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SectionLabel from "./SectionLabel";

const HEADLINE_WORDS: { text: string; highlight?: boolean }[] = [
  { text: "Intellivex", highlight: true },
  { text: "LTD", highlight: true },
  { text: "—", highlight: false },
  { text: "an", highlight: false },
  { text: "IT", highlight: false },
  { text: "company", highlight: false },
  { text: "specializing", highlight: false },
  { text: "in", highlight: false },
  { text: "developing", highlight: true },
  { text: "utility", highlight: true },
  { text: "apps", highlight: true },
  { text: "—", highlight: false },
  { text: "from", highlight: false },
  { text: "cleaners", highlight: false },
  { text: "and", highlight: false },
  { text: "optimizers", highlight: false },
  { text: "to", highlight: false },
  { text: "AI", highlight: true },
  { text: "services", highlight: false },
  { text: "—", highlight: false },
  { text: "and", highlight: false },
  { text: "their", highlight: false },
  { text: "promotion", highlight: true },
  { text: "and", highlight: false },
  { text: "advertising", highlight: true },
  { text: "support", highlight: false },
  { text: "turnkey.", highlight: true },
];

const TICKER_ITEMS = [
  "Mobile Utilities",
  "Desktop Apps",
  "AI Services",
  "ASO Promotion",
  "Media Buying",
  "IT Consulting",
  "UI/UX Design",
];

export default function IntroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMouse({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  const parallax = (strength: number) => ({
    transform: `translate3d(${(mouse.x - 0.5) * strength}px, ${
      (mouse.y - 0.5) * strength
    }px, 0)`,
  });

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000000",
        padding: "100px 16px 80px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* animated grid backdrop */}
      <div className="intro-grid-bg" />

      {/* floating soft orbs (mouse parallax) */}
      <div
        className="intro-orb"
        style={{
          width: 520,
          height: 520,
          background: "#2563EB",
          top: -180,
          left: -160,
          opacity: 0.35,
          animation: "introFloat 14s ease-in-out infinite",
          ...parallax(40),
        }}
      />
      <div
        className="intro-orb"
        style={{
          width: 380,
          height: 380,
          background: "#60A5FA",
          bottom: -140,
          right: -100,
          opacity: 0.28,
          animation: "introFloatB 18s ease-in-out infinite",
          ...parallax(-30),
        }}
      />
      <div
        className="intro-orb"
        style={{
          width: 240,
          height: 240,
          background: "#93C5FD",
          top: "40%",
          right: "12%",
          opacity: 0.12,
          animation: "introFloat 22s ease-in-out infinite reverse",
          ...parallax(60),
        }}
      />

      {/* rotating dashed rings */}
      <div
        className="intro-ring hidden lg:block"
        style={{
          width: 640,
          height: 640,
          top: "10%",
          left: "-8%",
          ...parallax(20),
        }}
      />
      <div
        className="intro-ring hidden lg:block"
        style={{
          width: 320,
          height: 320,
          bottom: "8%",
          right: "6%",
          animationDuration: "60s",
          animationDirection: "reverse",
          ...parallax(-15),
        }}
      />

      {/* cursor spotlight */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(600px circle at ${mouse.x * 100}% ${
            mouse.y * 100
          }%, rgba(37,99,235,0.14), transparent 60%)`,
          transition: "background 0.15s ease-out",
        }}
      />

      <div
        className="relative mx-auto flex flex-col lg:grid gap-8 lg:gap-10"
        style={{
          maxWidth: 1440,
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        }}
      >
        {/* Left column */}
        <div
          className="flex flex-col gap-6 lg:gap-10 order-1 lg:order-none"
          style={{ gridColumn: "span 3 / span 3" }}
        >
          <div
            className={`intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.05s" }}
          >
            <SectionLabel>Who we are — Intellivex LTD</SectionLabel>
          </div>

          {/* counter badge — hidden on mobile */}
          <div
            className={`hidden lg:block intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.55s" }}
          >
            <div
              className="relative rounded-2xl"
              style={{
                border: "1px solid rgba(255,255,255,0.09)",
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.04), rgba(37,99,235,0.08))",
                padding: "22px 20px",
                backdropFilter: "blur(6px)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#8A8A8A",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                On the market since 2020
              </div>
              <div
                style={{
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: "#C8C8C8",
                }}
              >
                We guide projects through every stage — from concept and analytics to development, launch, promotion, and ongoing support.
              </div>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 14,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: 9999,
                  background: "#3B82F6",
                  animation: "introDotPulse 2.4s ease-in-out infinite",
                }}
              />
            </div>
          </div>

          {/* vertical ticker — hidden on mobile */}
          <div
            className={`hidden lg:block intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.75s" }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#8A8A8A",
                letterSpacing: 0.5,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Our Expertise
            </div>
            <div
              style={{
                position: "relative",
                height: 24,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  animation: "verticalTicker 12s cubic-bezier(0.7, 0, 0.3, 1) infinite",
                }}
              >
                {TICKER_ITEMS.concat(TICKER_ITEMS[0]).map((t, i) => (
                  <div
                    key={i}
                    style={{
                      height: 24,
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#93C5FD",
                      letterSpacing: -0.2,
                    }}
                  >
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div
          className="order-2 lg:order-none"
          style={{ gridColumn: "span 9 / span 9" }}
        >
          <p
            style={{
              fontSize: "clamp(26px, 3.6vw, 54px)",
              lineHeight: 1.12,
              letterSpacing: "-0.02em",
              margin: 0,
              fontWeight: 500,
              perspective: 1200,
            }}
          >
            {HEADLINE_WORDS.map((w, i) => (
              <span
                key={i}
                className={`intro-word ${inView ? "is-in" : ""} ${
                  w.highlight ? "is-highlight" : ""
                }`}
                style={{
                  color: w.highlight ? "#ECECEC" : "#8A8A8A",
                  marginRight: "0.28em",
                  animationDelay: `${0.2 + i * 0.035}s`,
                }}
              >
                {w.text}
              </span>
            ))}
          </p>

          {/* buttons */}
          <div
            className={`mt-6 lg:mt-10 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 intro-fade-up ${
              inView ? "is-in" : ""
            }`}
            style={{ animationDelay: `${0.2 + HEADLINE_WORDS.length * 0.035 + 0.1}s` }}
          >
            <Link
              to="/our-products"
              className="whitespace-nowrap cursor-pointer intro-btn"
              style={{
                background: "#E9E9E9",
                color: "#000",
                border: "none",
                borderRadius: 9999,
                padding: "12px 22px",
                fontWeight: 600,
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                textDecoration: "none",
              }}
            >
              <span>Our products</span>
              <span
                className="intro-arrow"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                }}
              >
                <i className="ri-arrow-right-line" style={{ fontSize: 16 }} />
              </span>
              <span className="intro-btn__sweep" />
            </Link>

            <Link
              to="/contact-us"
              className="whitespace-nowrap cursor-pointer intro-btn intro-btn--ghost"
              style={{
                background: "transparent",
                color: "#ECECEC",
                border: "1px solid rgba(255,255,255,0.55)",
                borderRadius: 9999,
                padding: "11px 20px",
                fontWeight: 600,
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                textDecoration: "none",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: "#3B82F6",
                  display: "inline-block",
                  animation: "introDotPulse 2s ease-in-out infinite",
                }}
              />
              <span>Contact us</span>
              <span className="intro-btn__sweep" />
            </Link>

            {/* scroll hint — desktop only */}
            <div
              className="hidden lg:inline-flex"
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                color: "#8A8A8A",
                fontSize: 12,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <span>Scroll down</span>
              <span
                style={{
                  width: 28,
                  height: 44,
                  borderRadius: 9999,
                  border: "1px solid rgba(255,255,255,0.3)",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 6,
                    left: "50%",
                    width: 4,
                    height: 8,
                    borderRadius: 9999,
                    background: "#3B82F6",
                    transform: "translateX(-50%)",
                    animation: "scrollDot 1.8s ease-in-out infinite",
                  }}
                />
              </span>
            </div>
          </div>

          {/* Counter badge — mobile only */}
          <div
            className={`lg:hidden mt-6 intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.55s" }}
          >
            <div
              className="relative rounded-2xl"
              style={{
                border: "1px solid rgba(255,255,255,0.09)",
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.04), rgba(37,99,235,0.08))",
                padding: "18px 16px",
                backdropFilter: "blur(6px)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#8A8A8A",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                On the market since 2020
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "#C8C8C8",
                }}
              >
                We guide projects through every stage — from concept and analytics to development, launch, promotion, and ongoing support.
              </div>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 14,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: 9999,
                  background: "#3B82F6",
                  animation: "introDotPulse 2.4s ease-in-out infinite",
                }}
              />
            </div>
          </div>

          {/* Vertical ticker — mobile only: horizontal */}
          <div
            className={`lg:hidden mt-5 intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.75s" }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#8A8A8A",
                letterSpacing: 0.5,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Our Expertise
            </div>
            <div className="flex flex-wrap gap-2">
              {TICKER_ITEMS.map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#93C5FD",
                    letterSpacing: -0.2,
                    padding: "4px 12px",
                    borderRadius: 9999,
                    border: "1px solid rgba(147,197,253,0.25)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* mini stats row */}
          <div
            className={`mt-10 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 intro-fade-up ${
              inView ? "is-in" : ""
            }`}
            style={{
              animationDelay: `${0.2 + HEADLINE_WORDS.length * 0.035 + 0.25}s`,
            }}
          >
            {[
              { k: "50+", l: "Utility apps launched since 2020" },
              { k: "12M+", l: "Total app installs worldwide" },
              { k: "30+", l: "Countries with active users" },
              { k: "40+", l: "Specialists — developers, marketers, designers & QA" },
            ].map((s, i) => (
              <div
                key={s.k}
                className="group relative"
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                  paddingTop: 14,
                  transition: "border-color 0.4s ease",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(28px, 4vw, 44px)",
                    fontWeight: 500,
                    color: "#ECECEC",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                    transition: "color 0.35s ease, transform 0.35s ease",
                  }}
                  className="group-hover:!text-[#3B82F6] group-hover:!scale-105"
                >
                  {s.k}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: "#8A8A8A",
                  }}
                >
                  {s.l}
                </div>
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: -1,
                    left: 0,
                    height: 1,
                    width: "0%",
                    background: "#3B82F6",
                    transition: "width 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                  className="group-hover:!w-full"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes verticalTicker {
          0%, 12% { transform: translateY(0); }
          14%, 26% { transform: translateY(-24px); }
          28%, 40% { transform: translateY(-48px); }
          42%, 54% { transform: translateY(-72px); }
          56%, 68% { transform: translateY(-96px); }
          70%, 82% { transform: translateY(-120px); }
          84%, 96% { transform: translateY(-144px); }
          100% { transform: translateY(-168px); }
        }
        @keyframes scrollDot {
          0%, 100% { transform: translate(-50%, 0); opacity: 1; }
          50% { transform: translate(-50%, 18px); opacity: 0.2; }
        }
      `}</style>
    </section>
  );
}